import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { DataGridPro, koKR } from '@mui/x-data-grid-pro';
import {
  Box,
  Button,
  Chip,
  Container,
  CssBaseline,
  Grid,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { FormInputAutoComplete, FormInputText } from '../form';
import { gclientOptionLabel, renderGclientOption, toGclientSelectOption } from './gclientSelectOption';
import { CustomNoRowsOverlay } from '../datagrid';
import { getGridHeight } from '../../constants/gridHeights';
import * as subMatFolderActions from '../../store/subMatFolder';
import * as subMatFolderAlarmEmailActions from '../../store/subMatFolderAlarmEmail';
import * as gclientActions from '../../store/gclient';
import SubMatFolderExpiryDialog from './SubMatFolderExpiryDialog';
import SubMatFolderAlarmSettingDialog from './SubMatFolderAlarmSettingDialog';
import {
  formatSubMatEndDate,
  formatSubMatExpiryStatusText,
  getSubMatExpiryStatus,
  getSubMatExpiryStatusTextColor,
} from './SubMatFolderExpiryStatus';
import '../../utils/Date';

const theme = createTheme();
const GRID_HEIGHT = getGridHeight('ONE_LINE_LAYER', 0);

const STATUS_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'unset', label: '미설정' },
  { value: 'expiring', label: '임박(7일)' },
  { value: 'expired', label: '만료' },
  { value: 'valid', label: '유효' },
  { value: 'none', label: '만료일 없음' },
];

const ROOT_KIND_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'GENERAL', label: '일반문서' },
  { value: 'CERTIFICATION', label: '인증서' },
  { value: 'TEST', label: '성적서' },
  { value: 'ETC', label: '기타' },
];

const toExplorerFolderPath = (folderPath) => {
  if (!folderPath) return '';
  return String(folderPath)
    .split(/\s*(?:›|\/)\s*/)
    .filter(Boolean)
    .join(' › ');
};

const defaultValues = {
  gclient: null,
  rootKind: '',
  expiryStatus: '',
  fileName: '',
};

/**
 * 폴더형 서류 만료일 점검·알람설정 (DataGrid).
 * 기본: 자기 거래선. ADMIN: 거래처 선택 가능.
 */
const SubMatFolderExpiryReviewManagement = () => {
  const sessionUser = useSelector((state) => state.session.sessionUser);
  const isAdmin = sessionUser?.type === 'ADMIN';
  const { control, setValue, getValues } = useForm({ defaultValues });

  const [gclients, setGclients] = useState([]);
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [pageSize, setPageSize] = useState(100);
  const [expiryTarget, setExpiryTarget] = useState(null);
  const [alarmTarget, setAlarmTarget] = useState(null);
  const [error, setError] = useState('');

  const selfId = sessionUser?.id || sessionUser?.gclientId;

  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const list = await gclientActions.selectValidAllDirect();
        if (cancelled) return;
        setGclients(list || []);
        if (selfId) {
          const self = (list || []).find((c) => c.id === selfId);
          if (self) {
            setValue('gclient', toGclientSelectOption(self));
          }
        }
      } catch (_e) {
        if (!cancelled) setGclients([]);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, selfId, setValue]);

  const loadRows = useCallback(async () => {
    setLoaded(false);
    setError('');
    try {
      const gclient = getValues('gclient');
      const rootKind = getValues('rootKind');
      const expiryStatus = getValues('expiryStatus');
      const fileName = (getValues('fileName') || '').trim();
      const targetId = isAdmin ? (gclient?.id || selfId) : selfId;
      const list = await subMatFolderActions.selectExpiryReviewDirect({
        gclientId: targetId,
        rootKind: rootKind || undefined,
        expiryStatus: expiryStatus || undefined,
        fileName: fileName || undefined,
      });
      setRows((list || []).map((r) => ({
        ...r,
        id: r.id,
        expiryStatusKey: getSubMatExpiryStatus(r),
        endDateLabel: formatSubMatEndDate(r),
      })));
    } catch (e) {
      setRows([]);
      setError(e?.serverResponse?.message || e.message || '목록을 불러오지 못했습니다.');
    } finally {
      setLoaded(true);
    }
  }, [getValues, isAdmin, selfId]);

  useEffect(() => {
    if (!sessionUser) return;
    loadRows();
  }, [sessionUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClickSearch = () => {
    loadRows();
  };

  const handleChangeExpiryStatus = (e) => {
    setValue('expiryStatus', e.target.value);
    loadRows();
  };

  const handleChangeRootKind = (e) => {
    setValue('rootKind', e.target.value);
    loadRows();
  };

  const handleChangeGClient = (_e, newValue) => {
    setValue('gclient', newValue);
    loadRows();
  };

  const openAlarmDialog = useCallback(async (row) => {
    if (!row?.folderId) return;
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    try {
      const alarms = await subMatFolderAlarmEmailActions.selectDirect({
        gclientId: row.gclientId,
        folderId: row.folderId,
      });
      window.setTimeout(() => {
        setAlarmTarget({
          folderId: row.folderId,
          gclientId: row.gclientId,
          alarms: alarms || [],
        });
      }, 0);
    } catch (e) {
      setError(e?.serverResponse?.message || e.message || '알람 설정을 불러오지 못했습니다.');
    }
  }, []);

  const handleDeleteAlarm = useCallback(async (id) => {
    if (!id) return;
    try {
      await subMatFolderAlarmEmailActions.removeDirect(id);
      loadRows();
    } catch (e) {
      setError(e?.serverResponse?.message || e.message || '알람을 삭제하지 못했습니다.');
    }
  }, [loadRows]);

  const openExpiryDialog = useCallback((row) => {
    if (!row?.id) return;
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    // DataGrid 클릭 처리가 끝난 뒤 열어 포커스 충돌 완화
    window.setTimeout(() => {
      setExpiryTarget({
        file: { ...row },
        folderId: row.folderId,
      });
    }, 0);
  }, []);

  const columns = useMemo(() => {
    const cols = [];
    if (isAdmin) {
      cols.push({
        field: 'gclientName',
        headerName: '거래처',
        width: 160,
      });
    }
    cols.push(
      {
        field: 'rootKind',
        headerName: '서류종류',
        width: 100,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (params) => subMatFolderActions.ROOT_KIND_LABEL[params.value] || params.value || '',
      },
      {
        field: 'fileName',
        headerName: '파일명',
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'folderPath',
        headerName: '폴더 경로',
        flex: 1.2,
        minWidth: 220,
        valueGetter: (params) => toExplorerFolderPath(params.value),
        renderCell: (params) => {
          const path = params.value || '';
          if (!path) return '';
          const parts = path.split(' › ');
          return (
            <Tooltip title={path} followCursor>
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  minWidth: 0,
                  overflow: 'hidden',
                  fontSize: 'inherit',
                }}
              >
                {parts.map((name, i) => (
                  <Box
                    key={`${i}-${name}`}
                    component="span"
                    sx={{ display: 'inline-flex', alignItems: 'center', minWidth: 0 }}
                  >
                    {i > 0 && (
                      <Box component="span" sx={{ mx: 0.5, color: 'text.secondary', flexShrink: 0 }}>
                        ›
                      </Box>
                    )}
                    <Box
                      component="span"
                      sx={{
                        color: i === parts.length - 1 ? 'text.primary' : 'text.secondary',
                        fontWeight: i === parts.length - 1 ? 500 : 400,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {name}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: 'endDateLabel',
        headerName: '만료일',
        width: 120,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (params) => formatSubMatEndDate(params.row) || '',
      },
      {
        field: 'expiryStatusKey',
        headerName: '상태',
        width: 110,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (params) => formatSubMatExpiryStatusText(params.row) || '',
        renderCell: (params) => {
          const color = getSubMatExpiryStatusTextColor(params.row);
          return (
            <Box component="span" sx={color ? { color } : undefined}>
              {params.value}
            </Box>
          );
        },
      },
      {
        field: 'alarmsSet',
        headerName: '설정된 알람',
        width: 280,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const alarms = params.row.alarms;
          if (!alarms || !Array.isArray(alarms) || alarms.length === 0) return null;
          return alarms.map((alarm, i) => (
            <Chip
              key={alarm.id || `${alarm.type}-${alarm.period}-${i}`}
              label={subMatFolderAlarmEmailActions.formatAlarmPeriodLabel(alarm)}
              color="primary"
              size="small"
              variant="outlined"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDelete={() => handleDeleteAlarm(alarm.id)}
              sx={{ mr: i === alarms.length - 1 ? 0 : 1 }}
            />
          ));
        },
      },
      {
        field: 'actions',
        headerName: <Tooltip title="알람설정, 만료일 설정" followCursor><Box>기능</Box></Tooltip>,
        width: 150,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5} justifyContent="center">
            <Button
              variant="outlined"
              size="small"
              sx={{ fontSize: '0.75rem', py: 0, minWidth: 0, px: 1 }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                openAlarmDialog(params.row);
              }}
            >
              알람
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{ fontSize: '0.75rem', py: 0, minWidth: 0, px: 1 }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                openExpiryDialog(params.row);
              }}
            >
              만료일
            </Button>
          </Stack>
        ),
      },
    );
    return cols;
  }, [handleDeleteAlarm, isAdmin, openAlarmDialog, openExpiryDialog]);

  const gclientOptions = useMemo(
    () => (gclients || []).map((c) => toGclientSelectOption(c)),
    [gclients],
  );

  return (
    <>
      <ThemeProvider theme={theme}>
        <Container component="main" maxWidth="false">
          <CssBaseline />
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              {isAdmin && (
                <Grid item xs={12} sm={6} md={4} display="flex" justifyContent="flex-start" alignItems="center">
                  <FormInputAutoComplete
                    name="gclient"
                    control={control}
                    setValue={setValue}
                    label="거래처"
                    noOptionsText="해당 거래처가 없습니다."
                    options={gclientOptions}
                    getOptionLabel={gclientOptionLabel}
                    renderOption={renderGclientOption}
                    isOptionEqualToValue={(a, b) => a?.id === b?.id}
                    onCustomChange={handleChangeGClient}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={3} md={2} display="flex" justifyContent="flex-start" alignItems="center">
                <FormInputText
                  select
                  name="rootKind"
                  control={control}
                  label="서류종류"
                  options={ROOT_KIND_OPTIONS}
                  onChange={handleChangeRootKind}
                />
              </Grid>
              <Grid item xs={12} sm={3} md={2} display="flex" justifyContent="flex-start" alignItems="center">
                <FormInputText
                  select
                  name="expiryStatus"
                  control={control}
                  label="만료 상태"
                  options={STATUS_OPTIONS}
                  onChange={handleChangeExpiryStatus}
                />
              </Grid>
              <Grid item xs={12} sm={3} md={2} display="flex" justifyContent="flex-start" alignItems="center">
                <FormInputText
                  name="fileName"
                  control={control}
                  label="파일명으로 검색하세요."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleClickSearch();
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={isAdmin ? 2 : 3}
                md={isAdmin ? 3 : 6}
                container
                justifyContent="flex-end"
                alignItems="center"
              >
                <Button
                  variant="contained"
                  startIcon={<Search />}
                  onClick={handleClickSearch}
                  sx={{ ml: 5, mr: 1 }}
                >
                  조회
                </Button>
              </Grid>
            </Grid>

            {error && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>{error}</Typography>
            )}

            <div style={{ height: GRID_HEIGHT.FULL, width: '100%', marginTop: 10 }}>
              <DataGridPro
                localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
                sx={{
                  cursor: 'pointer',
                  fontSize: '0.85em',
                }}
                initialState={{ pinnedColumns: { right: ['actions'] } }}
                slots={{
                  noRowsOverlay: CustomNoRowsOverlay,
                  loadingOverlay: LinearProgress,
                }}
                headerHeight={38}
                rowHeight={34}
                loading={!loaded}
                getRowId={(row) => row.id}
                rows={rows}
                columns={columns}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                rowsPerPageOptions={[10, 20, 50, 100]}
                pagination
                disableRowSelectionOnClick
                onRowDoubleClick={(params) => openExpiryDialog(params.row)}
              />
            </div>
          </Box>
        </Container>
      </ThemeProvider>

      {/* DataGrid/ThemeProvider 밖에 두어 DatePicker 포커스 충돌 완화 (서류등록과 동일 계층) */}
      <SubMatFolderExpiryDialog
        open={Boolean(expiryTarget?.file)}
        file={expiryTarget?.file || null}
        folderId={expiryTarget?.folderId || expiryTarget?.file?.folderId}
        onClose={() => setExpiryTarget(null)}
        onSaved={() => {
          setExpiryTarget(null);
          loadRows();
        }}
      />
      <SubMatFolderAlarmSettingDialog
        open={Boolean(alarmTarget?.folderId)}
        folderId={alarmTarget?.folderId}
        gclientId={alarmTarget?.gclientId}
        alarms={alarmTarget?.alarms}
        onClose={() => setAlarmTarget(null)}
        onSaved={() => {
          setAlarmTarget(null);
          loadRows();
        }}
      />
    </>
  );
};

export default SubMatFolderExpiryReviewManagement;
